import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/ui/responsive-dialog';
import { toastError, toastSuccess } from '@/lib/toast-helpers';
import { useAuthStore } from '@/stores';

interface AuthDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

const RESEND_COOLDOWN_SECONDS = 60;

export function AuthDialog({ open, onOpenChange }: AuthDialogProps) {
  const { t } = useTranslation('common');
  const { email, otpSent, isLoading, error, signInWithEmail, verifyOtp, resetOtp } = useAuthStore();

  const [emailInput, setEmailInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  // Show error toast when auth error occurs
  useEffect(() => {
    if (error && open) {
      toastError(error || t('auth.error'));
    }
  }, [error, open, t]);

  // Countdown timer effect
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [resendCountdown]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim()) {
      toastError(t('auth.emailRequired'));
      return;
    }

    try {
      await signInWithEmail(emailInput.trim());
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      toastSuccess(t('auth.otpSent'));
    } catch {
      // Error is handled by the store
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpInput.trim() || !email) {
      toastError(t('auth.otpRequired'));
      return;
    }

    try {
      await verifyOtp(email, otpInput.trim());
      // Only show success and close dialog if verification succeeds
      toastSuccess(t('auth.signInSuccess'));
      onOpenChange(false);
      resetOtp();
      setEmailInput('');
      setOtpInput('');
    } catch {
      // Error is handled by the store and shown via useEffect
      // Dialog remains open so user can try again
    }
  };

  const handleResendOtp = async () => {
    if (!email || resendCountdown > 0) {
      return;
    }

    try {
      await signInWithEmail(email);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      toastSuccess(t('auth.otpResent'));
    } catch {
      // Error is handled by the store
    }
  };

  const handleCancel = () => {
    resetOtp();
    setEmailInput('');
    setOtpInput('');
    setResendCountdown(0);
    onOpenChange(false);
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={handleCancel}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader className="px-4 sm:px-0">
          <ResponsiveDialogTitle>
            {otpSent ? t('auth.verifyOtp') : t('auth.signIn')}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {otpSent ? (
              <>{t('auth.otpSentDescription').replace('{email}', email || '')}</>
            ) : (
              t('auth.signInDescription')
            )}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="flex flex-col gap-6 px-4 pb-4 sm:px-0 sm:pb-0">
          {otpSent ? (
            <form
              onSubmit={(e) => {
                void handleOtpSubmit(e);
              }}
              className="flex flex-col gap-6"
            >
              <div className="space-y-3">
                <Label htmlFor="otp" className="block text-center text-sm font-medium">
                  {t('auth.otpCode')}
                </Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpInput}
                    onChange={(value) => {
                      setOtpInput(value);
                    }}
                    disabled={isLoading}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-xs text-muted-foreground text-center">{t('auth.checkEmail')}</p>
                <div className="flex justify-center pt-1">
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => {
                      void handleResendOtp();
                    }}
                    disabled={isLoading || resendCountdown > 0}
                    className="text-xs"
                  >
                    {resendCountdown > 0
                      ? t('auth.resendIn', { seconds: resendCountdown })
                      : t('auth.resendCode')}
                  </Button>
                </div>
              </div>
              <ResponsiveDialogFooter className="gap-3 px-4 sm:flex-row sm:justify-end sm:px-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetOtp();
                    setResendCountdown(0);
                  }}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {t('auth.backToEmail')}
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !otpInput.trim()}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? t('auth.verifying') : t('auth.verify')}
                </Button>
              </ResponsiveDialogFooter>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                void handleEmailSubmit(e);
              }}
              className="flex flex-col gap-6"
            >
              <div className="space-y-3">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                  }}
                  placeholder={t('auth.emailPlaceholder')}
                  disabled={isLoading}
                  required
                  className="w-full"
                />
              </div>
              <ResponsiveDialogFooter className="gap-3 px-4 sm:flex-row sm:justify-end sm:px-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !emailInput.trim()}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? t('auth.sendingOtp') : t('auth.sendOtp')}
                </Button>
              </ResponsiveDialogFooter>
            </form>
          )}
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
